<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import paymentDocumentService, { type PaymentDocument } from '../services/payment-document.service'

const router = useRouter()
const route = useRoute()
const { t, locale } = useI18n()

const clubUuid = route.params.uuid as string
const documentType = route.params.type as 'terms' | 'privacy'

const versions = ref<PaymentDocument[]>([])
const selectedVersion = ref<PaymentDocument | null>(null)
const showVersionModal = ref(false)
const loading = ref(true)
const error = ref<string | null>(null)

// Computed
const pageTitle = computed(() => {
  return documentType === 'terms' ? t('paymentDocumentHistory.termsTitle') : t('paymentDocumentHistory.privacyTitle')
})

// Methods
const loadHistory = async () => {
  loading.value = true
  error.value = null

  try {
    const response = await paymentDocumentService.getDocumentHistory(clubUuid, documentType)
    versions.value = response.versions
  } catch (err: any) {
    console.error('Error loading document history:', err)
    error.value = err.response?.data?.message || t('paymentDocumentHistory.errorLoading')
  } finally {
    loading.value = false
  }
}

const viewVersion = (version: PaymentDocument) => {
  selectedVersion.value = version
  showVersionModal.value = true
}

const closeVersionModal = () => {
  showVersionModal.value = false
  selectedVersion.value = null
}

const goBack = () => {
  router.push(`/club/${clubUuid}/payment-settings`)
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleString(({ pl: 'pl-PL', en: 'en-US', fr: 'fr-FR', es: 'es-ES', de: 'de-DE', it: 'it-IT', pt: 'pt-PT', cs: 'cs-CZ', nl: 'nl-NL', sv: 'sv-SE', da: 'da-DK', hu: 'hu-HU', ro: 'ro-RO', no: 'nb-NO', lt: 'lt-LT', hr: 'hr-HR', fi: 'fi-FI' }[locale.value] || 'en-US'), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Lifecycle
onMounted(() => {
  loadHistory()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-6">
      <!-- Back Button -->
      <div class="mb-4">
        <button
          @click="goBack"
          class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('paymentDocumentHistory.backToSettings') }}
        </button>
      </div>

      <!-- Page Header -->
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-gray-900">{{ pageTitle }}</h1>
        <p class="text-sm text-gray-600 mt-1">{{ documentType === 'terms' ? t('paymentDocumentHistory.allVersionsTerms') : t('paymentDocumentHistory.allVersionsPrivacy') }}</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
        <div class="flex items-center justify-center py-12">
          <div class="text-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p class="text-gray-600">{{ t('paymentDocumentHistory.loading') }}</p>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-6">
        <p class="text-sm">{{ error }}</p>
        <button
          @click="loadHistory"
          class="mt-3 px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
        >
          {{ t('paymentDocumentHistory.retry') }}
        </button>
      </div>

      <!-- Versions List -->
      <div v-else class="bg-white border-2 border-gray-300 rounded shadow-sm">
        <!-- No versions -->
        <div v-if="versions.length === 0" class="p-6 text-center text-gray-500">
          {{ t('paymentDocumentHistory.noVersions') }}
        </div>

        <!-- Versions Table -->
        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="bg-gray-100 border-b-2 border-gray-300">
              <tr>
                <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('paymentDocumentHistory.version') }}</th>
                <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('paymentDocumentHistory.titleColumn') }}</th>
                <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('paymentDocumentHistory.statusColumn') }}</th>
                <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('paymentDocumentHistory.createdAt') }}</th>
                <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('paymentDocumentHistory.createdBy') }}</th>
                <th class="px-4 py-3 text-right font-semibold text-gray-700">{{ t('paymentDocumentHistory.actions') }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr
                v-for="version in versions"
                :key="version.uuid"
                class="hover:bg-gray-50"
              >
                <td class="px-4 py-3 font-medium text-gray-900">v{{ version.version }}</td>
                <td class="px-4 py-3 text-gray-900">{{ version.title }}</td>
                <td class="px-4 py-3">
                  <span
                    v-if="version.is_active"
                    class="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded border border-green-300"
                  >
                    {{ t('paymentDocumentHistory.active') }}
                  </span>
                  <span
                    v-else
                    class="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded border border-gray-300"
                  >
                    {{ t('paymentDocumentHistory.archived') }}
                  </span>
                </td>
                <td class="px-4 py-3 text-gray-700">{{ formatDate(version.created_at) }}</td>
                <td class="px-4 py-3 text-gray-700">{{ version.created_by?.email || '-' }}</td>
                <td class="px-4 py-3 text-right">
                  <button
                    @click="viewVersion(version)"
                    class="px-3 py-1.5 bg-blue-600 text-xs font-medium text-white rounded hover:bg-blue-700"
                  >
                    {{ t('paymentDocumentHistory.viewContent') }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Version Content Modal -->
    <Teleport to="body">
      <Transition name="modal">
        <div
          v-if="showVersionModal && selectedVersion"
          class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          @click.self="closeVersionModal"
        >
          <div class="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <!-- Modal Header -->
            <div class="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
              <div>
                <h2 class="text-xl font-semibold text-gray-900">{{ selectedVersion.title }}</h2>
                <div class="flex items-center gap-4 mt-1 text-sm text-gray-600">
                  <span>{{ t('paymentDocumentHistory.versionLabel') }} {{ selectedVersion.version }}</span>
                  <span v-if="selectedVersion.is_active" class="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">
                    {{ t('paymentDocumentHistory.active') }}
                  </span>
                  <span>{{ t('paymentDocumentHistory.createdAtLabel') }} {{ formatDate(selectedVersion.created_at) }}</span>
                </div>
              </div>
              <button
                @click="closeVersionModal"
                class="text-gray-400 hover:text-gray-600"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <!-- Modal Body -->
            <div class="p-6">
              <div class="prose max-w-none" v-html="selectedVersion.content"></div>
            </div>

            <!-- Modal Footer -->
            <div class="flex items-center justify-end p-6 border-t border-gray-200 sticky bottom-0 bg-white">
              <button
                @click="closeVersionModal"
                class="px-6 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
              >
                {{ t('paymentDocumentHistory.close') }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </main>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .bg-white,
.modal-leave-active .bg-white {
  transition: transform 0.3s ease;
}

.modal-enter-from .bg-white,
.modal-leave-to .bg-white {
  transform: scale(0.95);
}

/* Prose styles for document content */
.prose {
  color: #374151;
  line-height: 1.75;
}

.prose h1,
.prose h2,
.prose h3,
.prose h4,
.prose h5,
.prose h6 {
  color: #111827;
  font-weight: 600;
  margin-top: 1.5em;
  margin-bottom: 0.5em;
}

.prose p {
  margin-top: 0.75em;
  margin-bottom: 0.75em;
}

.prose ul,
.prose ol {
  margin-top: 0.75em;
  margin-bottom: 0.75em;
  padding-left: 1.5em;
}

.prose li {
  margin-top: 0.25em;
  margin-bottom: 0.25em;
}

.prose strong {
  font-weight: 600;
  color: #111827;
}

.prose a {
  color: #2563eb;
  text-decoration: underline;
}

.prose a:hover {
  color: #1d4ed8;
}
</style>
